import {isNullOrZero} from '@benchling/js-utils';
import Backbone from 'backbone';
import _ from 'underscore';

import {Sequence as BioJSSequence} from 'components/Sequence';
import {
  FolderSummary,
  MinimalFolder,
  ServerMinimalFolder,
  NumberIDFolder,
  FolderWithPermissions,
} from 'constants/FolderConstants';
import {IntentLevel} from 'file-browser/FileSelectorConstants';
import FileState from 'filestate/FileState';
import FileStates from 'FileStates';
import BaseFile from 'folder-items/BaseFile';
import BaseFolderItem from 'folder-items/BaseFolderItem';
import ProxyFile from 'folder-items/ProxyFile';
import * as net from 'util/Net';
import {Optional} from 'util/Types';

type FoldersById = Record<string, ServerMinimalFolder>;

interface XHRCache {
  [folderId: string]: {
    [params: string]: JQueryPromise<FolderWithPermissions> | JQueryPromise<FolderSummary> | undefined;
  };
}

interface XHRALLCache {
  [key: string]: JQueryPromise<Array<MinimalFolder>>;
}

interface FolderParents {
  [id: string]: string | undefined;
}

interface ListenToFileInput<ModelType, ReturnType> {
  field: string;
  get?: (model: ModelType) => ReturnType;
}

interface ListenToFileInputVersions {
  field: string;
  get: (version?: number) => number | null;
}

type CacheFileTypes = FileState | BaseFile | BaseFolderItem | BioJSSequence | typeof ProxyFile;

type CacheFileTypesExcludingProxyFile = Exclude<CacheFileTypes, typeof ProxyFile>;

class FolderDAOCache {
  FOLDER_CACHE_INVALIDATE: string = 'invalidated';
  cachedFetchXHR: XHRCache;
  cachedFetchAllXHR: XHRALLCache;
  parents: FolderParents;

  static initClass(): void {
    this.prototype.FOLDER_CACHE_INVALIDATE = 'invalidated';

    // indirect implementation of Backbone.Events to avoid "Method not implemented" clutter
    // eslint-disable-next-line no-restricted-syntax
    _.extend(FolderDAOCache.prototype, Backbone.Events);
  }

  constructor() {
    this.cachedFetchXHR = {}; // maps [folderId][params] -> fetch xhr
    this.cachedFetchAllXHR = {}; // maps [type+level] -> fetchAll xhr
    this.parents = {}; // maps [folderId] -> parentFolderId
  }

  listenToFileStates(fileStates: FileStates): void {
    this.listenTo(fileStates, 'add', this.addFileState.bind(this));
    this.listenTo(fileStates, 'reset', this.resetFileStates.bind(this));
    this.listenTo(fileStates, 'remove', this.removeFileState.bind(this));
    fileStates.each((fileState) => {
      this.addFileState(fileState);
    });
  }

  // FolderDAOCache implements Backbone.Events indirectly through _.extend
  listenTo(
    _fileStates: FileStates | CacheFileTypes,
    _arg1: string,
    addFileState: (fileState: FileState) => void
  ): void {
    throw new Error('Method not implemented.');
  }

  private listenToFile(
    file: CacheFileTypes,
    {
      name,
      folder,
      openVersion,
      savedVersion,
    }: {
      name: ListenToFileInput<string | typeof ProxyFile, string>;
      folder: ListenToFileInput<NumberIDFolder | MinimalFolder, string | undefined>;
      openVersion: ListenToFileInputVersions;
      savedVersion: ListenToFileInputVersions;
    }
  ): void {
    // eslint-disable-next-line no-restricted-syntax
    const events = _.uniq([name, folder, openVersion, savedVersion].map((attr) => `change:${attr.field}`));
    let openVersionWithUpdatedName: number | null = null;
    this.listenTo(file, events.join(' '), function () {
      // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      const check = (attr: any): any => ({
        prev: attr.get((file as CacheFileTypesExcludingProxyFile).previous(attr.field)),
        // @ts-expect-error
        next: attr.get((file as CacheFileTypesExcludingProxyFile).get(attr.field)),
      });
      const {prev: prevFolder, next: newFolder} = check(folder);
      const {prev: prevName, next: newName} = check(name);
      const {next: newSavedVersion} = check(savedVersion);
      const {next: newOpenVersion} = check(openVersion);
      const hasOptimisticUpdates = newOpenVersion != null;

      // NOTE: We don't invalidate on savedVersion changes even though this will affect the modifiedAt
      // when listing the folder's contents: it simply happens too frequently (which will cause downstream
      // code to do frequent re-searches), and in most cases the user is likely not sorting by modifiedAt.

      if (hasOptimisticUpdates) {
        // If a name change may have been optimistic, track what open version it was introduced on.
        if (prevName !== newName) {
          openVersionWithUpdatedName = newOpenVersion;
        }
        // Once that version is acknowledged, we can invalidate the cache.
        if (openVersionWithUpdatedName != null && openVersionWithUpdatedName <= newSavedVersion) {
          openVersionWithUpdatedName = null;
          // @ts-expect-error
          this.invalidateFolderContents(newFolder);
        }
      } else if (prevName !== newName) {
        // For regular files, the update is not optimistic, so we can immediately invalidate.
        // @ts-expect-error
        this.invalidateFolderContents(newFolder);
      }

      if (prevFolder !== newFolder) {
        // Invalidate file's previous folder and new folder
        // @ts-expect-error
        this.invalidateFolderContents(prevFolder);
        // @ts-expect-error
        this.invalidateFolderContents(newFolder);
      }
    });
  }

  private resetFileStates(fileStates: Array<FileState>, options: {previousModels: Array<FileState>}): void {
    for (const fileState of Array.from(options.previousModels)) {
      this.removeFileState(fileState);
    }
  }

  private removeFileState(fileState: FileState): void {
    /**
     * NOTE: "require Sequence" as opposed to "import Sequence" is what makes FolderDAOCache.ts pass integration tests.
     * This is very strange behavior and a code smell that needs refactoring (not to mention investigating) at some point.
     */
    const {Sequence} = require('components/Sequence');
    if (!fileState.isFolderItem()) {
      return;
    }
    // @ts-expect-error
    const model = fileState.model();
    if (model instanceof Sequence) {
      // @ts-expect-error
      this.stopListening(model.get('_sequence'));
    } else {
      // @ts-expect-error
      this.stopListening(model);
    }
  }

  private addFileState(fileState: FileState): void {
    if (!fileState.isFolderItem()) {
      return;
    }
    // @ts-expect-error
    const model = fileState.model();
    const {Sequence} = require('components/Sequence');
    // NB only ProxyFile has a savedVersion field that changes, but the others are left as no-ops.
    if (model instanceof Sequence) {
      this.listenToFile(model.get('_sequence'), {
        name: {
          field: 'name',
          get(name: string) {
            return name;
          },
        },
        folder: {
          field: 'folder',
          get(folder: NumberIDFolder) {
            return folder != null ? folder.api_identifier : undefined;
          },
        },
        savedVersion: {
          field: 'savedVersion',
          get() {
            return null;
          },
        },
        openVersion: {
          field: 'openVersion',
          get() {
            return null;
          },
        },
      });
    } else if (model instanceof ProxyFile) {
      this.listenToFile(model, {
        name: {
          field: model.proxiedField,
          get(proxied: typeof ProxyFile) {
            return proxied.name;
          },
        },
        folder: {
          field: model.proxiedField,
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          get(proxied: any) {
            return proxied.folder.api_identifier;
          },
        },
        savedVersion: {
          field: 'savedVersion',
          get(version: number) {
            return version;
          },
        },
        openVersion: {
          field: 'openVersion',
          get(version: number) {
            return version;
          },
        },
      });
    } else {
      this.listenToFile(model, {
        name: {
          field: 'name',
          get(name: string) {
            return name;
          },
        },
        folder: {
          field: 'folder',
          get(folder: MinimalFolder) {
            return folder.api_identifier;
          },
        },
        savedVersion: {
          field: 'savedVersion',
          get() {
            return null;
          },
        },
        openVersion: {
          field: 'openVersion',
          get() {
            return null;
          },
        },
      });
    }
  }

  private addParents(folders: Array<ServerMinimalFolder>): void {
    const foldersById: FoldersById = {};
    folders.forEach((f) => {
      foldersById[f.api_identifier] = f;
    });
    folders.forEach((f) => {
      f.parent = f.parentId ? foldersById[f.parentId] || null : null;
    });
  }

  cachedFetchAll(
    type: string,
    level: IntentLevel,
    {refetch = false}: {refetch?: boolean} = {}
  ): JQueryPromise<Array<MinimalFolder>> {
    const key = `${type}-${level}`;
    if (!refetch && this.cachedFetchAllXHR[key] != null) {
      return this.cachedFetchAllXHR[key];
    }
    // If the request is not in the cache, make a new request
    const xhr: JQueryPromise<Array<MinimalFolder>> = net
      .getJSON(`/1/api/minimal-folders`, {type, level})
      .done((folders: Array<ServerMinimalFolder>) => {
        this.addParents(folders);
        return folders;
      });

    // Maintain cache
    this.cachedFetchAllXHR[key] = xhr;
    xhr.fail(() => {
      delete this.cachedFetchAllXHR[key];
    });
    return xhr;
  }

  private registerFetch(id: string, key: string, xhr: JQueryPromise<FolderSummary>): void {
    if (this.cachedFetchXHR[id] == null) {
      this.cachedFetchXHR[id] = {};
    }
    // If we are doing a refetch, we only replace the existing XHR after the refetch is complete.
    // Otherwise, clicking back and forth quickly will result in losing the cached response on refetch
    if (this.cachedFetchXHR[id][key] != null) {
      xhr.done((resp) => {
        if (resp != null && this.cachedFetchXHR[id] != null) {
          this.cachedFetchXHR[id][key] = xhr;
        } else {
          this.cachedFetchXHR[id][key] = undefined;
        }
      });
    } else {
      this.cachedFetchXHR[id][key] = xhr;
    }
    xhr.done((resp) => {
      if (resp != null && resp.parent != null) {
        this.parents[id] = resp.parent.api_identifier;
      } else {
        this.parents[id] = undefined;
      }
    });
  }

  cachedFetchSummary(
    id: string,
    params: {summary: string | number; offset: number},
    param?: {refetch?: boolean; clear?: boolean}
  ): JQueryPromise<FolderSummary> | undefined {
    if (param == null) {
      param = {};
    }
    const {refetch, clear} = param;
    const key = JSON.stringify(params);

    if (this.cachedFetchXHR[id] == null) {
      this.cachedFetchXHR[id] = {};
    }

    if (this.cachedFetchXHR[id][key] != null && !refetch && !clear) {
      return this.cachedFetchXHR[id][key];
    }

    // If the request is not in the cache, make a new request
    const xhr: JQueryPromise<FolderSummary> = net.getJSON(`/1/api/folders/${id}`, params);

    // Don't cache if nonzero offset is specified
    if (isNullOrZero(params.offset)) {
      this.registerFetch(id, key, xhr);
    }

    return xhr;
  }

  cachedFetchWithPermissions(
    id: string,
    params: {offset: number},
    param?: {refetch?: boolean; clear?: boolean}
  ): JQueryPromise<FolderWithPermissions> {
    if (param == null) {
      param = {};
    }
    const {refetch, clear} = param;
    const key = JSON.stringify(params);

    if (this.cachedFetchXHR[id] == null) {
      this.cachedFetchXHR[id] = {};
    }

    if (this.cachedFetchXHR[id][key] != null && !refetch && !clear) {
      return this.cachedFetchXHR[id][key] as JQueryPromise<FolderWithPermissions>;
    }

    // If the request is not in the cache, make a new request
    const xhr: JQueryPromise<FolderWithPermissions> = net.getJSON(`/1/api/folders/${id}`, params);

    // Don't cache if nonzero offset is specified
    if (isNullOrZero(params.offset)) {
      this.registerFetch(id, key, xhr);
    }

    return xhr;
  }

  invalidateFetchAll(): void {
    this.cachedFetchAllXHR = {};
  }

  invalidateFolder(id: string | null, options?: unknown): void {
    if (options == null) {
      options = {};
    }
    this.invalidateFolderContents(id, options);
    this.invalidateFetchAll();
  }

  invalidateFolderContents(id: Optional<string | number>, options?: unknown): void {
    if (id == null) {
      return;
    }
    if (options == null) {
      options = {};
    }
    delete this.cachedFetchXHR[id];
    delete this.parents[id];
    this.trigger(this.FOLDER_CACHE_INVALIDATE, id, options);
  }

  // Backbone.Events method, implemented indirectly through _.extend
  trigger(FOLDER_CACHE_INVALIDATE: string, id: Optional<string | number>, options: unknown): void {
    throw new Error('Method not implemented.');
  }

  invalidateAllFolders(): void {
    Object.keys(this.cachedFetchXHR).forEach((id) => {
      this.invalidateFolder(id);
    });
  }
}
FolderDAOCache.initClass();

// implements Backbone.Events indirectly through _.extend
const folderDAOCache = new FolderDAOCache() as FolderDAOCache & Backbone.Events;
export default folderDAOCache;
